/**
 * Shared timestamp formatters used by the chat message list (ChatPanel) and the
 * user sidebar (RightSidebar). Extracted so the relative-time logic lives in one
 * place rather than being duplicated per component.
 */

/**
 * Formats a timestamp as a coarse relative time:
 *   < 1 min   → "Now"
 *   < 60 min  → "Nm"        (1m … 59m)
 *   < 24 hr   → "Nh" / "Nh Mm"
 *   >= 24 hr  → "Nd" / "Nd Mh"
 * Accepts a Date, an ISO string, or epoch ms. Returns '' for missing/invalid.
 *
 * @param {Date|string|number} timestamp
 * @param {number} [now] reference "now" in ms (defaults to Date.now()); passing
 *   it lets a caller format many rows against a single clock read.
 */
export const formatRelativeTime = (timestamp, now = Date.now()) => {
  if (!timestamp) return '';
  const then =
    timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
  if (Number.isNaN(then)) return '';

  // Clamp negatives (clock skew between client and server) to 0.
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return 'Now';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;

  const hours = Math.floor(diffMin / 60);
  if (hours < 24) {
    const mins = diffMin % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

/**
 * Formats a timestamp as locale clock time (e.g. "2:34:56 PM"). Returns '' for
 * missing/invalid input. This is the historical message-timestamp format.
 *
 * @param {Date|string|number} timestamp
 */
export const formatClockTime = (timestamp) => {
  if (!timestamp) return '';
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString();
};
